<template>
  <div
    class="d-flex justify-content-center justify-content-sm-between align-items-center text-center flex-wrap gap-2 showing-wrap"
  >
    <span class="fs-12 fw-medium">
      Showing {{ items }} of {{ total }} Results
    </span>

    <nav aria-label="Page navigation example">
      <ul class="pagination mb-0 justify-content-center">
        <li :class="['page-item', { disabled: currentPage === 1 }]">
          <a
            class="page-link icon"
            href="#"
            @click.prevent="prevPage"
            aria-label="Previous"
          >
            <i class="material-symbols-outlined">keyboard_arrow_left</i>
          </a>
        </li>

        <li
          v-for="page in paginatedPages"
          :key="page"
          :class="['page-item', { active: page === currentPage }]"
        >
          <a
            v-if="page !== '...'"
            class="page-link"
            href="#"
            @click.prevent="goToPage(page)"
          >
            {{ page }}
          </a>
          <span v-else class="page-link disabled">...</span>
        </li>

        <li :class="['page-item', { disabled: currentPage === totalPages }]">
          <a
            class="page-link icon"
            href="#"
            @click.prevent="nextPage"
            aria-label="Next"
          >
            <i class="material-symbols-outlined">keyboard_arrow_right</i>
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from "vue";

export default defineComponent({
  name: "PaginationPage",
  props: {
    items: {
      type: Number,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
  },
  emits: ["page-change"],
  setup(props, { emit }) {
    const currentPage = ref(1);
    const totalPages = computed(() => Math.ceil(props.total / props.items));

    const paginatedPages = computed(() => {
      const pages = [];
      const firstPage = 1;
      const lastPage = totalPages.value;

      if (lastPage <= 5) {
        for (let i = 1; i <= lastPage; i++) pages.push(i);
      } else {
        const startPage = Math.max(currentPage.value - 2, 2);
        const endPage = Math.min(currentPage.value + 2, lastPage - 1);

        pages.push(firstPage);
        if (startPage > 2) pages.push("...");

        for (let i = startPage; i <= endPage; i++) pages.push(i);

        if (endPage < lastPage - 1) pages.push("...");
        pages.push(lastPage);
      }

      return pages;
    });

    const goToPage = (page: number) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        emit("page-change", page);
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) goToPage(currentPage.value - 1);
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) goToPage(currentPage.value + 1);
    };

    return {
      currentPage,
      totalPages,
      paginatedPages,
      goToPage,
      prevPage,
      nextPage,
    };
  },
});
</script>
